import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import {
  fetchInvitationDetail,
  updateInvStatus,
  requestRiskOverride,
  approveRiskOverride,
  rejectRiskOverride,
  clearCurrentInvitation,
  saveCRAComment,
} from "../Redux/feasibility";
import { sfqApi } from "../../../api/feasibility";
import { hasPermission } from "../../../utils/hasPermission";
import { PERM } from "../../../utils/permissions";
import {
  groupBySection, SECTIONS, RISK_COLOR, SCORE_DOMAINS, computeHiddenKeys,
  YN_TYPES, isSpecifyShown, optionHasSpecify, isStudyFrozen,
  branchNo,
  STATUS_LABELS as SFQ_STATUS_LABELS,
} from "../FeasibilityEditor/data";
import CRAAssessmentPanel from "./CRAAssessmentPanel";
import { craAssessmentEnabled } from "../StudyView/CraAssessmentToggle";
import { consentRequired } from "../StudyView/ConsentToggle";

// ── Styles ────────────────────────────────────────────────────────────────────

const Wrap = styled.div``;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 20px;
  padding: 16px;
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 6px;
`;

const SiteMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const SiteName = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: #212529;
`;

const MetaLine = styled.div`
  font-size: 12px;
  color: #6c757d;
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 600;
  background: ${(p) => STATUS_STYLE[p.$s]?.bg || "#e9ecef"};
  color: ${(p) => STATUS_STYLE[p.$s]?.color || "#495057"};
`;

// 점수 패널이 없는 스터디(CRA Assessment 미사용)에서는 응답 표가 폭을 다 쓴다.
const Grid = styled.div`
  display: grid;
  grid-template-columns: ${(p) => (p.$single ? "1fr" : "1fr 300px")};
  gap: 20px;
  align-items: start;
`;

const SectionWrap = styled.div`
  margin-bottom: 24px;
`;

const SectionHeader = styled.div`
  background: #e9ecef;
  padding: 8px 12px;
  font-weight: 600;
  font-size: 13px;
  border: 1px solid #dee2e6;
  border-bottom: none;
  border-radius: 4px 4px 0 0;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
`;

const Th = styled.th`
  padding: 7px 10px;
  background: #f1f3f5;
  border: 1px solid #dee2e6;
  text-align: left;
  font-size: 12px;
  color: #495057;
  white-space: nowrap;
`;

const Td = styled.td`
  padding: 8px 10px;
  border: 1px solid #dee2e6;
  vertical-align: top;
  font-size: 13px;
`;

// 하위 문항의 Question 칸. 안내선은 셀의 border가 아니라 padding 안쪽에 그린다 —
// border-collapse 표에서 셀 테두리는 옆 칸과 공유하는 표선이라, 그걸 건드리면 계층이
// 아니라 표가 깨진 것처럼 보인다.
const SubTd = styled(Td)`
  padding-left: 28px;
  position: relative;
  &::before {
    content: "";
    position: absolute;
    left: 13px; top: 8px; bottom: 8px;
    border-left: 2px solid #dee2e6;
  }
`;

const ScoreWrap = styled.div`
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 16px;
  background: #fff;
`;

const ScoreTitle = styled.div`
  font-weight: 600;
  font-size: 14px;
  margin-bottom: 12px;
`;

const DomainRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  font-size: 12px;
`;

const DomainLabel = styled.div`
  width: 130px;
  color: #495057;
  flex-shrink: 0;
`;

const BarTrack = styled.div`
  flex: 1;
  height: 8px;
  background: #e9ecef;
  border-radius: 4px;
  overflow: hidden;
`;

const BarFill = styled.div`
  height: 100%;
  border-radius: 4px;
  width: ${(p) => p.$score}%;
  background: ${(p) =>
    p.$score >= 85 ? "#6f1a1a" : p.$score >= 70 ? "#dc3545" : p.$score >= 40 ? "#fd7e14" : "#198754"};
`;

const RiskBadge = styled.span`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 600;
  background: ${(p) => (RISK_COLOR[p.$r] || "#6c757d") + "22"};
  color: ${(p) => RISK_COLOR[p.$r] || "#6c757d"};
  border: 1px solid ${(p) => RISK_COLOR[p.$r] || "#6c757d"};
`;

const ActionBar = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 16px;
`;

const CommentTextarea = styled.textarea`
  width: 100%;
  min-height: 56px;
  font-size: 12px;
  border: 1px solid #dee2e6;
  border-radius: 3px;
  padding: 4px 6px;
  resize: vertical;
  box-sizing: border-box;
  color: #212529;
  background: #fafafa;
  &:focus {
    outline: none;
    border-color: #86b7fe;
    background: #fff;
  }
`;

const Btn = styled.button`
  padding: 5px 14px;
  border-radius: 4px;
  border: 1px solid ${(p) => p.$color || "#0d6efd"};
  background: ${(p) => p.$filled ? (p.$color || "#0d6efd") : "#fff"};
  color: ${(p) => p.$filled ? "#fff" : (p.$color || "#0d6efd")};
  font-size: 13px;
  cursor: pointer;
  &:disabled { opacity: 0.4; cursor: not-allowed; }
  &:hover:not(:disabled) { filter: brightness(0.92); }
`;

const STATUS_STYLE = {
  Pending:   { bg: "#e9ecef", color: "#495057" },
  Sent:      { bg: "#cfe2ff", color: "#084298" },
  InProgress:{ bg: "#fff3cd", color: "#664d03" },
  Submitted: { bg: "#d1e7dd", color: "#0a3622" },
  Reviewed:  { bg: "#d0d6f9", color: "#1d3367" },
  Finalized: { bg: "#d1ecf1", color: "#0c5460" },
};

const STATUS_LABELS = {
  Pending: "Pending", Sent: "Sent", InProgress: "In Progress",
  Submitted: "Submitted", Reviewed: "Reviewed", Finalized: "Finalized",
};

const REASON_MAX = 1000; // SFQ_AuditTrail.comment

// Both forward steps are confirmed: each one freezes work that only a rollback reopens,
// so neither should ride on a single stray click.
const CONFIRM_MESSAGE = {
  Reviewed:  "검토 완료 후 평가 및 질문별 코멘트는 수정할 수 없습니다.\n" +
             "수정이 필요한 경우 사유 입력 후 Submitted 상태로 되돌릴 수 있습니다.",
  Finalized: "이 사이트 평가를 Finalize 합니다. 진행할까요?",
};

// Mirrors INV_ROLLBACK in services/Feasibility/index.js: both states unwind to
// Submitted, the only one where the assessment is editable again — Reviewed is frozen,
// so stopping there would just cost a second rollback. The forward legs are hardcoded
// here too, so this file already keeps the graph in sync with the server by hand — the
// server is the one that enforces it.
const ROLLBACK_TARGET = {
  Reviewed:  "Submitted",
  Finalized: "Submitted",
};

// Panel copy per rollback leg: the two legs reopen the same editing, but undoing a
// Finalize also drops what Finalize created — so that leg gets its own warning and a
// longer path back (Mark Reviewed, then Finalize again).
const ROLLBACK_COPY = {
  Reviewed: {
    title: "검토 완료 취소",
    lines: [
      "Reviewed 상태를 취소하고 Submitted 상태로 변경합니다.",
      "평가 및 질문별 코멘트를 다시 수정할 수 있습니다.",
      "수정 후 검토를 완료하려면 Mark Reviewed 절차를 다시 진행해야 합니다.",
    ],
  },
  Finalized: {
    title: "최종 확정 취소",
    lines: [
      "Finalized 상태를 취소하면 Submitted 상태로 변경됩니다.",
      "평가 및 질문별 코멘트를 다시 수정할 수 있습니다.",
      "수정 완료 후에는 Mark Reviewed를 다시 진행한 뒤 Finalize해야 합니다.",
    ],
    warning: "주의: Finalize 시점에 생성된 스냅샷 및 재평가 기록은 삭제됩니다.",
  },
};

const DOMAINS = SCORE_DOMAINS;

const RISK_LEVELS = ["Low", "Medium", "High", "Critical"];

function fmtDate(val) {
  if (!val) return "—";
  return new Date(val).toLocaleString("ko-KR", {
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit",
  });
}

// 개인정보 동의 한 줄. 미동의가 경고가 되는 것은 아직 응답할 수 있는 초대뿐이다 — 응답이
// 끝난 초대에 기록이 없다면 그 사이트는 동의 절차가 생기기 전에 응답한 것이고, 지금 와서
// 받을 수 있는 동의도 아니다. (서버도 같은 기준으로 게이트를 세운다.)
const CONSENT_PENDING_STATUSES = ["Pending", "Sent", "InProgress"];

function consentLine(inv) {
  if (inv.consentGiven) {
    return `✔ 개인정보 동의: ${fmtDate(inv.consentAt)}${inv.consentBy ? ` · ${inv.consentBy}` : ""}`;
  }
  return CONSENT_PENDING_STATUSES.includes(inv.status)
    ? "⚠ 개인정보 미동의 — Site가 동의해야 설문이 열립니다."
    : "개인정보 동의 기록 없음 (동의 절차 도입 전 응답)";
}

const consentTone = (inv) =>
  inv.consentGiven ? "#15803d"
    : CONSENT_PENDING_STATUSES.includes(inv.status) ? "#b45309" : "#adb5bd";

// ── Site answer renderer ──────────────────────────────────────────────────────

function parseJsonSafe(str, fallback) {
  try { return JSON.parse(str || ""); } catch { return fallback; }
}

const YN_DISPLAY = {
  Y:  { color: "#0f5132", text: "✓ Yes" },
  N:  { color: "#842029", text: "✗ No" },
  NA: { color: "#495057", text: "– N/A" },
};

function SiteAnswerCell({ question, ans }) {
  const a = ans || {};
  if (YN_TYPES.includes(question.type)) {
    const shown = YN_DISPLAY[a.answerYN];
    return (
      <>
        <span style={{
          color: shown ? shown.color : "#adb5bd",
          fontWeight: shown ? 600 : 400,
        }}>
          {shown ? shown.text : "—"}
        </span>
        {isSpecifyShown(question, a) && a.specifyText && (
          <div style={{ marginTop: 4, fontSize: 12, color: "#495057", paddingLeft: 8,
                        borderLeft: "2px solid #dee2e6" }}>
            {a.specifyText}
          </div>
        )}
      </>
    );
  }
  if (question.type === "numeric") {
    return <span>{a.answerNumeric ?? "—"}</span>;
  }
  if (question.type === "checklist") {
    const opts     = question.options || [];
    const checked  = parseJsonSafe(a.answerText, []);
    const specMap  = parseJsonSafe(a.specifyText, {});
    if (!opts.length) return <span style={{ color: "#adb5bd" }}>—</span>;
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        {opts.map((opt) => {
          const isChecked = checked.includes(opt.key);
          return (
            <div key={opt.key}>
              <span style={{
                color:      isChecked ? "#212529" : "#adb5bd",
                fontWeight: isChecked ? 500 : 400,
              }}>
                {isChecked ? "☑" : "☐"} {opt.label}
              </span>
              {isChecked && optionHasSpecify(opt) && specMap[opt.key] && (
                <div style={{ fontSize: 12, color: "#495057", paddingLeft: 18,
                              borderLeft: "2px solid #dee2e6", marginTop: 2 }}>
                  {specMap[opt.key]}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }
  return <span>{a.answerText || "—"}</span>;
}

// ── Main component ────────────────────────────────────────────────────────────

const TabBar = styled.div`
  display: flex;
  gap: 2px;
  border-bottom: 2px solid #dee2e6;
  margin-bottom: 16px;
`;

const Tab = styled.button`
  padding: 6px 16px;
  border: none;
  background: transparent;
  font-size: 13px;
  cursor: pointer;
  border-bottom: 2px solid ${(p) => (p.$active ? "#0d6efd" : "transparent")};
  color: ${(p) => (p.$active ? "#0d6efd" : "#495057")};
  margin-bottom: -2px;
  &:hover { color: #0d6efd; }
`;

// Inline CRA comment cell with debounce autosave
function CRACommentCell({ sfqId, invId, questionKey, initialValue, canEdit }) {
  const dispatch = useDispatch();
  const [value, setValue] = useState(initialValue || "");
  const timerRef   = useRef(null);
  const pendingRef = useRef(null);

  // sync when invitation reloads
  useEffect(() => { setValue(initialValue || ""); }, [initialValue]);

  const save = useCallback(async (text) => {
    clearTimeout(timerRef.current);
    timerRef.current   = null;
    pendingRef.current = null;
    try {
      await dispatch(saveCRAComment({ sfqId, invId, questionKey, commentText: text })).unwrap();
    } catch (e) {
      // Refused once the invitation is Reviewed — reachable by racing the debounce
      // against Mark Reviewed. Say so rather than losing the text silently.
      alert(e?.message || "코멘트 저장에 실패했습니다.");
    }
  }, [dispatch, sfqId, invId, questionKey]);

  const handleChange = useCallback((e) => {
    const text = e.target.value;
    setValue(text);
    clearTimeout(timerRef.current);
    pendingRef.current = text;
    timerRef.current = setTimeout(() => save(text), 700);
  }, [save]);

  // Flush on blur, cancel on unmount. Firing a stale timer after the cell is gone would
  // post a comment the user can no longer see — and, if the status moved on meanwhile,
  // one the server now refuses. Blurring happens first on any real navigation, so the
  // text is already saved by then.
  const handleBlur = useCallback(() => {
    if (pendingRef.current != null) save(pendingRef.current);
  }, [save]);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  if (!canEdit) {
    return <span style={{ color: value ? "#212529" : "#adb5bd", fontSize: 12 }}>{value || "—"}</span>;
  }
  return (
    <CommentTextarea
      value={value}
      onChange={handleChange}
      onBlur={handleBlur}
      placeholder="Comment..."
    />
  );
}

export default function InvitationViewer({ sfqId, invId }) {
  const dispatch    = useDispatch();
  const inv         = useSelector((s) => s.feasibility.currentInvitation);
  const permissions = useSelector((s) => s.userInfo.permissions);
  const me          = useSelector((s) => s.userInfo.employeeNo);

  const isStudyEdit = hasPermission(permissions, PERM.SFQ_STUDY_EDIT);
  // 이 화면은 스터디를 따로 싣지 않으므로, 초대 상세가 스터디의 owner·reviewer·CRA를 함께 내려준다.
  // Owner(createdBy)는 초대 Finalize와 리스크 재정의 승인을 한다.
  const isOwner = isStudyEdit && !!inv?.createdBy && String(inv.createdBy) === String(me);
  // 배정된 CRA 또는 owner가 사이트 작업(코멘트·평가·Mark Reviewed·리스크 재정의 요청)을 한다.
  const isAssignedCRA = !!(inv?.cras || []).some((no) => String(no) === String(me));
  // 스터디가 Finalize 이후로 종료됐으면 이 초대도 함께 닫힌다 — 초대 상세가 스터디 상태를
  // studyStatus로 함께 내려준다(getInvitationDetail). 서버도 422로 막는다.
  const studyFrozen = isStudyFrozen(inv?.studyStatus);
  const canWork = (isOwner || isAssignedCRA) && !studyFrozen;

  const [tab, setTab]           = useState("response");
  const [exporting, setExporting] = useState(false);
  const [exportingWord, setExportingWord] = useState(false);
  const [ovRisk, setOvRisk]     = useState("");
  const [ovReason, setOvReason] = useState("");
  const [ovBusy, setOvBusy]     = useState(false);
  // Withdraw/reject of a pending override — the reason box only opens for a rejection.
  const [rejOpen, setRejOpen]     = useState(false);
  const [rejReason, setRejReason] = useState("");
  const [rejBusy, setRejBusy]     = useState(false);
  const [statusBusy, setStatusBusy] = useState(false);
  const [rbOpen, setRbOpen]     = useState(false);
  const [rbReason, setRbReason] = useState("");

  useEffect(() => {
    dispatch(fetchInvitationDetail({ sfqId, invId }));
    return () => { dispatch(clearCurrentInvitation()); };
  }, [sfqId, invId, dispatch]); // eslint-disable-line

  const answerMap = useMemo(() => {
    if (!inv?.answers) return {};
    return inv.answers.reduce((m, a) => { m[a.questionKey] = a; return m; }, {});
  }, [inv?.answers]);

  // Questions come from the version pinned to this invitation (getInvitationDetail),
  // so the reviewer sees exactly what the site received — not the live working copy.
  const sections = useMemo(
    () => (inv?.questions?.length ? groupBySection(inv.questions) : SECTIONS),
    [inv?.questions]
  );


  const hiddenQuestionKeys = useMemo(
    () => computeHiddenKeys(sections, answerMap),
    [sections, answerMap]
  );

  // 자리표시자는 아직 보여줄 초대가 없을 때만 — 이미 그려진 화면을 다시 읽는 동안까지
  // Loading으로 갈아끼우면 화면 전체가 언마운트됐다 다시 붙는다. CRA 평가 패널은 입력값을
  // 자기 state로 들고 있어서, 저장 직후의 새로고침 한 번에 커서와 스크롤 위치가 날아간다.
  // 초대를 바꿀 때는 clearCurrentInvitation이 먼저 비우므로 이전 초대가 남지 않는다.
  if (!inv) {
    return <div style={{ color: "#6c757d", fontSize: 13 }}>Loading...</div>;
  }

  const effectiveRisk = inv.riskOverride && !inv.riskOverridePendingPM
    ? inv.riskOverride : inv.riskLevel;

  // 스터디가 CRA Assessment를 쓰지 않으면(SFQ.craAssessmentEnabled) 평가 탭도 점수 패널도
  // 없다. 탭 상태를 되돌리는 대신 보여줄 탭을 여기서 고른다 — 평가 탭을 열어둔 채 스터디
  // 설정이 바뀌어도 빈 화면에 머물지 않는다. 문항별 CRA 코멘트는 사이트 응답에 대한 주석이라
  // 이 스위치와 무관하게 남는다.
  const craEnabled = craAssessmentEnabled(inv);
  const activeTab  = craEnabled ? tab : "response";

  async function handleStatus(newStatus, reasonText, confirmOverride) {
    const message =
      confirmOverride ||
      CONFIRM_MESSAGE[newStatus] ||
      `상태를 ${STATUS_LABELS[newStatus] || newStatus}(으)로 변경할까요?`;
    if (!window.confirm(message)) return false;

    setStatusBusy(true);
    try {
      await dispatch(updateInvStatus({ sfqId, invId: inv.id, newStatus, reason: reasonText })).unwrap();
      // A rollback out of Finalized also drops the finalize snapshot and can change
      // server-derived fields the reducer does not track; refetch rather than guess.
      dispatch(fetchInvitationDetail({ sfqId, invId: inv.id }));
      return true;
    } catch (e) {
      alert(e?.message || "상태 변경에 실패했습니다.");
      return false;
    } finally {
      setStatusBusy(false);
    }
  }

  const rollbackTo    = ROLLBACK_TARGET[inv.status];
  const rbCopy        = ROLLBACK_COPY[inv.status];
  const trimmedRb     = rbReason.trim();
  const rbInvalid     = !trimmedRb || trimmedRb.length > REASON_MAX;
  // Undoing Finalize undoes the owner's own act, so only the owner offers it; reopening
  // CRA work is offered to an assigned CRA too. The server enforces the same split, and
  // also refuses either leg while a risk override is awaiting the owner. `canWork` already
  // carries the study freeze, but the Finalized leg goes by `isOwner` — hence the explicit
  // check, since a closed-out study reopens nothing.
  const canRollback =
    !!rollbackTo
    && !inv.riskOverridePendingPM
    && !studyFrozen
    && (inv.status === "Finalized" ? isOwner : canWork);

  async function submitRollback() {
    if (rbInvalid) return;
    const dropsSnapshot = inv.status === "Finalized";
    const ok = await handleStatus(
      rollbackTo,
      trimmedRb,
      `상태를 ${STATUS_LABELS[inv.status]} → ${STATUS_LABELS[rollbackTo]}(으)로 되돌립니다. ` +
        `평가와 코멘트가 다시 열리며, 재확정하려면 Mark Reviewed부터 다시 거쳐야 합니다. ` +
        (dropsSnapshot ? "Finalize 시점 스냅샷과 리스크 재정의 기록은 삭제됩니다. " : "") +
        `사유와 함께 감사 추적에 기록됩니다. 진행할까요?`
    );
    if (!ok) return;
    setRbOpen(false);
    setRbReason("");
  }

  // Finalized 이후에만 Override 경로 사용 — 그 전에는 Final Assessment에서 자유 수정.
  // Finalized 이후의 모든 리스크 변경은 Owner 승인 필수.
  const craCanOverride = canWork && !!inv.riskLevel && inv.status === "Finalized";

  // A pending request has two exits besides approval: its requester withdraws it, the
  // owner rejects it with a reason. The API decides which leg this is from who calls —
  // this only picks the wording and whether to demand a reason.
  const isOverrideRequester =
    !!inv.riskOverrideBy && String(inv.riskOverrideBy) === String(me);
  // Same reason as canRollback: this leg goes by isOwner/requester rather than canWork.
  const canClearOverride =
    inv.riskOverridePendingPM === 1 && !studyFrozen && (isOwner || isOverrideRequester);
  const clearIsReject    = !isOverrideRequester;
  const trimmedRej       = rejReason.trim();

  async function submitClearOverride() {
    if (clearIsReject && !trimmedRej) return;
    if (trimmedRej.length > REASON_MAX) return;
    setRejBusy(true);
    try {
      const res = await dispatch(rejectRiskOverride({
        sfqId, invId: inv.id, reason: trimmedRej || null,
      })).unwrap();
      setRejOpen(false);
      setRejReason("");
      // Rejected, but the requester's notification may have failed to send.
      if (res?.emailWarning) alert(res.emailWarning);
    } catch (e) {
      alert(e?.message || "요청 처리에 실패했습니다.");
    } finally {
      setRejBusy(false);
    }
  }

  async function submitOverride() {
    if (!ovRisk || !ovReason.trim()) return;
    setOvBusy(true);
    try {
      const res = await dispatch(requestRiskOverride({
        sfqId, invId: inv.id, overrideRisk: ovRisk, reason: ovReason.trim(),
      })).unwrap();
      setOvRisk("");
      setOvReason("");
      // Override saved, but the owner notification email may have failed to send.
      if (res?.emailWarning) alert(res.emailWarning);
    } catch (e) {
      alert(e?.message || "Override request failed");
    } finally {
      setOvBusy(false);
    }
  }

  async function handleExportPDF() {
    setExporting(true);
    try {
      const res = await sfqApi.exportPDF(sfqId, inv.id);
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a   = document.createElement("a");
      a.href    = url;
      a.download = `SFQ_${inv.siteCode || inv.id}_${new Date().toISOString().slice(0, 10)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert("PDF export failed: " + (e.response?.data?.message || e.message));
    } finally {
      setExporting(false);
    }
  }

  async function handleExportWord() {
    setExportingWord(true);
    try {
      const res = await sfqApi.exportWord(sfqId, inv.id);
      const url = URL.createObjectURL(new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      }));
      const a   = document.createElement("a");
      a.href    = url;
      a.download = `SFQ_${inv.siteCode || inv.id}_${new Date().toISOString().slice(0, 10)}.docx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert("Word export failed: " + (e.response?.data?.message || e.message));
    } finally {
      setExportingWord(false);
    }
  }

  return (
    <Wrap>
      {/* Header */}
      <Header>
        <SiteMeta>
          <SiteName>{inv.siteName || inv.siteCode || "Unknown Site"}</SiteName>
          {inv.piName && <MetaLine>PI: {inv.piName} {inv.piEmail && `<${inv.piEmail}>`}</MetaLine>}
          {inv.crcName && <MetaLine>CRC: {inv.crcName} {inv.crcEmail && `<${inv.crcEmail}>`}</MetaLine>}
          {inv.siteCountry && <MetaLine>Country: {inv.siteCountry}</MetaLine>}
          <div style={{ marginTop: 6, display: "flex", gap: 12, fontSize: 12, color: "#6c757d" }}>
            <span>Sent: {fmtDate(inv.sentAt)}</span>
            <span>Submitted: {fmtDate(inv.submittedAt)}</span>
          </div>
          {/* 개인정보 동의 상태. 동의를 받지 않는 스터디에서는 줄 자체를 감추고, 이미 받아둔
              동의가 있으면 스터디가 나중에 껐더라도 계속 보여준다 — 내보내기와 같은 규칙.
              "아직 동의하지 않았다"는 말은 아직 응답할 수 있는 초대에서만 참이다. 이미 제출된
              초대에 동의 기록이 없는 것은 동의 기능 이전에 발송된 건이라는 뜻이므로, 재촉이
              아니라 사실만 적는다. */}
          {(consentRequired(inv) || inv.consentGiven) && (
            <div style={{ marginTop: 4, fontSize: 12, color: consentTone(inv) }}>
              {consentLine(inv)}
            </div>
          )}
        </SiteMeta>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 8 }}>
          <StatusBadge $s={inv.status}>
            {STATUS_LABELS[inv.status] || inv.status}
          </StatusBadge>

          {/* 내보내기는 읽기이므로 조회 권한자면 누구나 받을 수 있다 — QA는 감사에, owner는
              자신이 확정할 스터디에 쓴다. 상태를 옮기는 버튼만 자격으로 가린다. */}
          <ActionBar>
            {canWork && inv.status === "Submitted" && (
              <Btn $filled disabled={statusBusy} onClick={() => handleStatus("Reviewed")}>
                Mark Reviewed
              </Btn>
            )}
            {isOwner && !studyFrozen && inv.status === "Reviewed" && (
              <Btn $filled $color="#198754" disabled={statusBusy}
                onClick={() => handleStatus("Finalized")}>
                Finalize
              </Btn>
            )}
            {canRollback && (
              <Btn $color="#fd7e14" disabled={statusBusy}
                onClick={() => { setRbOpen((v) => !v); setRbReason(""); }}>
                {STATUS_LABELS[rollbackTo]}(으)로 되돌리기
              </Btn>
            )}
            <Btn $color="#6c757d" disabled={exporting} onClick={handleExportPDF}>
              {exporting ? "Exporting..." : "Export PDF"}
            </Btn>
            <Btn $color="#0d6efd" disabled={exportingWord} onClick={handleExportWord}>
              {exportingWord ? "Exporting..." : "Export Word"}
            </Btn>
          </ActionBar>
        </div>
      </Header>

      {/* Rollback reason — full width rather than in the header's right column, since a
          reason worth auditing needs room to write. */}
      {canRollback && rbOpen && (
        <div style={{ marginBottom: 20, padding: 12, border: "1px solid #fd7e14",
                      borderRadius: 6, background: "#fff9f3" }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
            {rbCopy.title}({STATUS_LABELS[inv.status]} → {STATUS_LABELS[rollbackTo]})
          </div>
          <div style={{ fontSize: 12, color: "#6c757d", marginBottom: 8, lineHeight: 1.6 }}>
            {rbCopy.lines.map((line) => <div key={line}>{line}</div>)}
            {rbCopy.warning && (
              <div style={{ marginTop: 6, color: "#dc3545" }}>{rbCopy.warning}</div>
            )}
            <div style={{ marginTop: 6 }}>계속 진행하려면 변경 사유를 입력해 주세요.</div>
          </div>
          <textarea value={rbReason} disabled={statusBusy}
            placeholder="되돌리는 사유 (필수)..."
            onChange={(e) => setRbReason(e.target.value)}
            style={{ width: "100%", boxSizing: "border-box", fontSize: 12, padding: "6px 8px",
                     border: "1px solid #dee2e6", borderRadius: 4, minHeight: 54, resize: "vertical" }} />
          {trimmedRb.length > REASON_MAX && (
            <div style={{ fontSize: 12, color: "#dc3545", marginTop: 4 }}>
              사유는 {REASON_MAX}자를 넘을 수 없습니다.
            </div>
          )}
          <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
            <Btn $filled $color="#fd7e14" disabled={statusBusy || rbInvalid}
              onClick={submitRollback}>
              {statusBusy ? "처리 중..." : "되돌리기"}
            </Btn>
            <Btn $color="#6c757d" disabled={statusBusy}
              onClick={() => { setRbOpen(false); setRbReason(""); }}>
              취소
            </Btn>
          </div>
        </div>
      )}

      {studyFrozen && (
        <div style={{ padding: "9px 14px", marginBottom: 12, fontSize: 12.5, color: "#343a40",
                      background: "#f1f3f5", borderLeft: "3px solid #495057", borderRadius: 3 }}>
          이 Study는 <b>{SFQ_STATUS_LABELS[inv.studyStatus] || inv.studyStatus}</b> 상태로
          종료되어 <b>읽기 전용</b>입니다. 조회와 PDF/Word 내보내기만 가능하며, 상태 변경과
          평가·코멘트는 할 수 없습니다.
        </div>
      )}

      <TabBar>
        <Tab $active={activeTab === "response"} onClick={() => setTab("response")}>Site Response</Tab>
        {craEnabled && (
          <Tab $active={activeTab === "cra"} onClick={() => setTab("cra")}>Assessment</Tab>
        )}
      </TabBar>

      {activeTab === "cra" && (
        <CRAAssessmentPanel
          sfqId={sfqId}
          inv={inv}
          isCRA={canWork}
          onSaved={() => dispatch(fetchInvitationDetail({ sfqId, invId: inv.id }))}
        />
      )}

      {activeTab === "response" && <Grid $single={!craEnabled}>
        {/* Questions */}
        <div>
          {Object.keys(sections).sort((a, b) => Number(a) - Number(b)).map((sec) => {
            const { title, questions: qs } = sections[sec];
            return (
              <SectionWrap key={sec}>
                <SectionHeader>
                  Section {sec}{title ? `: ${title}` : ""}
                </SectionHeader>
                <Table>
                  <thead>
                    <tr>
                      <Th style={{ width: 64 }}>No.</Th>
                      <Th style={{ width: "40%" }}>Question</Th>
                      <Th>Site Response</Th>
                      <Th style={{ width: "20%" }}>Comment</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {qs.map((q) => {
                      if (hiddenQuestionKeys.has(q.key)) return null;
                      return (
                        <tr key={q.key}>
                          {/* 하위 문항은 "3-1"처럼 상위 번호를 앞에 달고 나온다. 자기
                              번호만으로는 상위마다 1부터 다시 시작해 바로 위 문항과 같은
                              숫자가 되고, 들여쓰기만으로는 어느 상위에 딸렸는지 애매하다. */}
                          {/* 하위 문항은 "┖ 1"로 찍고, 번호도 문항 문구와 같이 한 단계
                              들여쓴다. 번호만 왼쪽에 붙어 있으면 계층이 문항 칸에서만
                              일어나 두 칸이 어긋나 보인다. */}
                          <Td style={{ textAlign: "left", whiteSpace: "nowrap",
                                       paddingLeft: q.parentKey ? 24 : 10, color: "#6c757d" }}>
                            {branchNo(q)}
                          </Td>
                          {q.parentKey
                            ? <SubTd style={{ width: "40%" }}>{q.text}</SubTd>
                            : <Td style={{ width: "40%" }}>{q.text}</Td>}
                          <Td>
                            <SiteAnswerCell question={q} ans={answerMap[q.key]} />
                          </Td>
                          <Td>
                            {/* Comments freeze with the rest of the review pass at
                                Reviewed — see CRA_REVIEW_EDIT_STATUSES server-side. */}
                            <CRACommentCell
                              sfqId={sfqId}
                              invId={inv.id}
                              questionKey={q.key}
                              initialValue={answerMap[q.key]?.commentText}
                              canEdit={canWork && inv.status === "Submitted"}
                            />
                          </Td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </SectionWrap>
            );
          })}
        </div>

        {/* Score Panel — 점수·리스크는 CRA Final Assessment의 산물이므로, 평가를
            쓰지 않는 스터디에서는 패널 자체가 없다. */}
        {craEnabled && <ScoreWrap>
          <ScoreTitle>Score Summary</ScoreTitle>
          {inv.scoreFinal != null ? (
            <>
              {DOMAINS.map((d) => {
                const score = inv[d.key] ?? 0;
                return (
                  <DomainRow key={d.key}>
                    <DomainLabel>
                      {d.label}
                      {/* <span style={{ color: "#adb5bd", fontSize: 10, marginLeft: 4 }}>
                        ({d.weight})
                      </span> */}
                    </DomainLabel>
                    <BarTrack><BarFill $score={score} /></BarTrack>
                    <span style={{ fontSize: 12, width: 34, textAlign: "right", color: "#495057" }}>
                      {score.toFixed(1)}
                    </span>
                  </DomainRow>
                );
              })}
              <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid #dee2e6",
                            display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontWeight: 600, fontSize: 13 }}>
                  Final: {inv.scoreFinal.toFixed(1)}
                </span>
                {effectiveRisk && <RiskBadge $r={effectiveRisk}>{effectiveRisk} Risk</RiskBadge>}
              </div>
              {inv.riskOverride && (
                <div style={{ marginTop: 6, fontSize: 12, color: "#6c757d" }}>
                  Override: <b>{inv.riskOverride}</b>
                  {inv.riskOverridePendingPM && " (pending PM)"}
                  {inv.riskOverrideApprovedBy && " ✓"}
                  {inv.riskOverrideReason && ` — ${inv.riskOverrideReason}`}
                </div>
              )}

              {/* Finalized 이후 리스크 변경 요청 (Owner 승인 필수) */}
              {craCanOverride && (
                <div style={{ marginTop: 12, paddingTop: 10, borderTop: "1px solid #dee2e6" }}>
                  <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>
                    Post-Finalization Risk Change {inv.riskLevel && <>(current: <b>{inv.riskLevel}</b>)</>}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                    <span style={{ fontSize: 12 }}>Change to:</span>
                    <select value={ovRisk} disabled={ovBusy}
                      onChange={(e) => setOvRisk(e.target.value)}
                      style={{ fontSize: 12, padding: "4px 6px", border: "1px solid #dee2e6", borderRadius: 4 }}>
                      <option value="">Select...</option>
                      {RISK_LEVELS.filter((r) => r !== inv.riskLevel).map((r) => (
                        <option key={r} value={r}>{r}</option>
                      ))}
                    </select>
                  </div>
                  <textarea value={ovReason} disabled={ovBusy}
                    placeholder="Reason for change (required)..."
                    onChange={(e) => setOvReason(e.target.value)}
                    style={{ width: "100%", boxSizing: "border-box", fontSize: 12, padding: "6px 8px",
                             border: "1px solid #dee2e6", borderRadius: 4, minHeight: 54, resize: "vertical" }} />
                  <div style={{ fontSize: 12, color: "#fd7e14", marginTop: 4 }}>
                    Finalized 이후 변경 — Owner 승인 필요.
                  </div>
                  <Btn $filled style={{ marginTop: 6 }}
                    disabled={ovBusy || !ovRisk || !ovReason.trim()}
                    onClick={submitOverride}>
                    {ovBusy ? "Submitting..." : "Request Change"}
                  </Btn>
                </div>
              )}

              {/* Owner approves; the requester withdraws, the owner rejects. A pending
                  request blocks the invitation's rollback legs, so it must be possible to
                  settle it either way from here. */}
              {canClearOverride && (
                <div style={{ marginTop: 12, paddingTop: 10, borderTop: "1px solid #dee2e6" }}>
                  <div style={{ fontSize: 12, marginBottom: 6 }}>
                    Override approval: <b>{inv.riskLevel}</b> → <b>{inv.riskOverride}</b>
                  </div>
                  <div style={{ display: "flex", gap: 6 }}>
                    {isOwner && (
                      <Btn $filled $color="#198754" disabled={rejBusy || rejOpen}
                        onClick={() => dispatch(approveRiskOverride({ sfqId, invId: inv.id }))}>
                        Approve
                      </Btn>
                    )}
                    <Btn $color="#dc3545" disabled={rejBusy}
                      onClick={() => {
                        // Withdrawing your own request costs nothing to explain; the
                        // owner turning down someone else's opens the reason box.
                        if (!clearIsReject) return submitClearOverride();
                        setRejOpen((v) => !v);
                        setRejReason("");
                      }}>
                      {clearIsReject ? "반려" : "요청 취소"}
                    </Btn>
                  </div>

                  {rejOpen && clearIsReject && (
                    <div style={{ marginTop: 8 }}>
                      <textarea value={rejReason} disabled={rejBusy}
                        placeholder="반려 사유 (필수) — 요청자에게 전달되며 감사 추적에 기록됩니다."
                        onChange={(e) => setRejReason(e.target.value)}
                        style={{ width: "100%", boxSizing: "border-box", fontSize: 12,
                                 padding: "6px 8px", border: "1px solid #dee2e6",
                                 borderRadius: 4, minHeight: 54, resize: "vertical" }} />
                      {trimmedRej.length > REASON_MAX && (
                        <div style={{ fontSize: 12, color: "#dc3545", marginTop: 4 }}>
                          사유는 {REASON_MAX}자를 넘을 수 없습니다.
                        </div>
                      )}
                      <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
                        <Btn $filled $color="#dc3545"
                          disabled={rejBusy || !trimmedRej || trimmedRej.length > REASON_MAX}
                          onClick={submitClearOverride}>
                          {rejBusy ? "처리 중..." : "반려하기"}
                        </Btn>
                        <Btn $color="#6c757d" disabled={rejBusy}
                          onClick={() => { setRejOpen(false); setRejReason(""); }}>
                          취소
                        </Btn>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            <div style={{ color: "#adb5bd", fontSize: 13 }}>
              No score yet — site has not submitted.
            </div>
          )}
        </ScoreWrap>}
      </Grid>}

    </Wrap>
  );
}
